import { useState } from "react";
import { ghostBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import { pageOverheadBucketRoots } from "@/lib/manufacturing/overheadBucketTaxonomy";
import { paymentUsd } from "@/lib/manufacturing/overheadObligation";
import type { MfgOverheadPeriodBucketRow } from "@/lib/manufacturing/overheadTypes";
import { OverheadPeriodValueEditor } from "./OverheadPeriodValueEditor";
import type { useManufacturingOverheadPeriodValues } from "./useManufacturingOverheadPeriodValues";

const CHILD_PAGE_SIZE = 5;
type PeriodValuesState = ReturnType<typeof useManufacturingOverheadPeriodValues>;

function formatUsd(value: number): string {
  return value.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function folderTotal(folder: MfgOverheadPeriodBucketRow, items: MfgOverheadPeriodBucketRow[]): string {
  const childAmounts = items
    .map((row) => paymentUsd(row.assumed_value_usd, row.actual_value_usd))
    .filter((amount): amount is number => amount != null);
  if (childAmounts.length > 0) {
    return formatUsd(childAmounts.reduce((sum, amount) => sum + amount, 0));
  }
  const own = paymentUsd(folder.assumed_value_usd, folder.actual_value_usd);
  return own == null ? "No amounts yet" : formatUsd(own);
}

function debtLeftLabel(folder: MfgOverheadPeriodBucketRow, items: MfgOverheadPeriodBucketRow[]): string {
  const rows = folder.tracks_balance ? [folder, ...items] : items;
  let total = 0;
  let any = false;
  for (const row of rows) {
    if (row.remaining_balance_usd == null) continue;
    any = true;
    total += row.remaining_balance_usd;
  }
  return any ? ` · ${formatUsd(total)} left` : "";
}

export function OverheadPeriodValueFolder({
  folder,
  items,
  state,
}: {
  folder: MfgOverheadPeriodBucketRow;
  items: MfgOverheadPeriodBucketRow[];
  state: PeriodValuesState;
}) {
  const [open, setOpen] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const page = pageOverheadBucketRoots(items, pageIndex, CHILD_PAGE_SIZE);
  const tag = folder.category.trim() || "folder";

  return (
    <div className="space-y-2">
      <button
        type="button"
        className="flex w-full items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-left text-sm dark:border-zinc-700"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <span aria-hidden="true" className="text-gray-500 dark:text-zinc-400">{open ? "▾" : "▸"}</span>
        <span className="min-w-0 truncate font-medium text-gray-900 dark:text-zinc-100">{folder.bucket_name}</span>
        <span className="shrink-0 rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-gray-700 dark:bg-zinc-700 dark:text-zinc-200">
          {tag}
        </span>
        <span className="ml-auto shrink-0 text-xs text-gray-600 dark:text-zinc-300">
          {folderTotal(folder, items)}
          {debtLeftLabel(folder, items)}
          {items.length > 0 ? ` · ${items.length}` : ""}
        </span>
      </button>
      {open ? (
        <div className="min-w-0 space-y-2 pl-3">
          {folder.is_aggregate ? null : <OverheadPeriodValueEditor row={folder} state={state} bare />}
          {page.page.map((row) => (
            <OverheadPeriodValueEditor key={row.bucket_id} row={row} state={state} />
          ))}
          {items.length > CHILD_PAGE_SIZE ? (
            <div className="flex items-center justify-end gap-2 text-xs text-gray-600 dark:text-zinc-400">
              <span>{page.rangeStart}–{page.rangeEnd} of {page.total}</span>
              <button type="button" className={ghostBtnClass} disabled={page.pageIndex <= 0} onClick={() => setPageIndex(page.pageIndex - 1)}>
                Previous
              </button>
              <span>{page.pageIndex + 1} / {page.pageCount}</span>
              <button type="button" className={ghostBtnClass} disabled={page.pageIndex + 1 >= page.pageCount} onClick={() => setPageIndex(page.pageIndex + 1)}>
                Next
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
